import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { EmptyState, Pagination, Panel, SearchInput, Spinner, Toolbar } from './AdminUI';
import { SEARCH_GROUPS, SearchResultRow } from './searchResults';

const PAGE_SIZE = 25;
const PREVIEW = 5;

function SearchResultsTab({ condo }) {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);

  const q = params.get('q') || '';
  const type = SEARCH_GROUPS.some((g) => g.type === params.get('type')) ? params.get('type') : '';
  const page = Number(params.get('page')) || 1;

  useEffect(() => {
    if (!q.trim()) {
      setData({ results: {}, counts: {}, totalResults: 0 });
      return;
    }
    let stale = false;
    setData(null);
    api.get(`/api/admin/condos/${condo._id}/search`, { q })
      .then((response) => { if (!stale) setData(response.data); })
      .catch((err) => {
        if (stale) return;
        alert(errorMessage(err, 'Search failed'));
        setData({ results: {}, counts: {}, totalResults: 0 });
      });
    return () => { stale = true; };
  }, [api, condo._id, q]);

  const groups = SEARCH_GROUPS.filter((g) => data?.results?.[g.type]?.length);
  const shown = type ? SEARCH_GROUPS.filter((g) => g.type === type) : groups;

  const renderGroup = (group) => {
    const items = data.results[group.type] || [];
    const total = data.counts[group.type] || 0;
    const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const current = Math.min(page, pages);
    const visible = type ? items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE) : items.slice(0, PREVIEW);
    return (
      <section key={group.type} aria-label={group.label} className="mb-6 last:mb-0">
        <div className="flex items-center gap-2 mb-1 px-3">
          <h2 className="text-sm font-semibold text-foreground">{group.label}</h2>
          <span className="text-xs text-muted-foreground">{total}</span>
          {!type && total > PREVIEW && (
            <button type="button" onClick={() => setParams({ type: group.type, page: null })} className="ml-auto text-sm text-primary hover:underline">Show all {total}</button>
          )}
        </div>
        {visible.length === 0 ? <EmptyState>No {group.label.toLowerCase()} match.</EmptyState> : (
          <div role="listbox" aria-label={`${group.label} results`}>
            {visible.map((item) => <SearchResultRow key={item._id} group={group} item={item} slug={condo.slug} onSelect={navigate} />)}
          </div>
        )}
        {type && (
          <>
            <Pagination page={current} pages={pages} total={items.length} limit={PAGE_SIZE} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
            {total > items.length && <p className="px-3 pt-2 text-xs text-muted-foreground">Showing the first {items.length} of {total}. Add words to narrow the search.</p>}
          </>
        )}
      </section>
    );
  };

  return (
    <Panel>
      <Toolbar>
        <SearchInput value={q} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search this condo..." label="Search terms" />
      </Toolbar>
      {data && data.totalResults > 0 && (
        <div role="group" aria-label="Result types" className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            aria-pressed={!type}
            onClick={() => setParams({ type: null, page: null })}
            className={`text-xs px-3 py-1 rounded-full border border-border ${!type ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            All · {data.totalResults}
          </button>
          {groups.map((g) => (
            <button
              key={g.type}
              type="button"
              aria-pressed={type === g.type}
              onClick={() => setParams({ type: g.type, page: null })}
              className={`text-xs px-3 py-1 rounded-full border border-border ${type === g.type ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {g.label} · {data.counts[g.type]}
            </button>
          ))}
        </div>
      )}
      {!data ? <Spinner /> : !q.trim() ? (
        <EmptyState>Type to search tickets (open and closed), residents, units, bulletins, events, forum posts, the handbook and amenities.</EmptyState>
      ) : data.totalResults === 0 ? (
        <EmptyState>No results for “{q}”.</EmptyState>
      ) : shown.map(renderGroup)}
    </Panel>
  );
}

export default SearchResultsTab;
